import { __assign } from 'tslib';
import * as React from 'react';
import { useRef, useImperativeHandle } from 'react';
import { UitkButton } from 'uitk-react-button';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkMenu, UitkMenuTrigger, UitkMenuContainer, PositionType, UitkMenuList } from 'uitk-react-menu';
import { classNames } from 'uitk-react-utils';
import { useLocalization } from '@shared-ui/localization-context';
import styles from './uitk-breadcrumbs.css';

var getClassNameList = classNames.bind(styles);
var BreadcrumbsMenu = function (_a) {
    var crumbs = _a.crumbs, analytics = _a.analytics, isOpen = _a.isOpen, onTriggerClick = _a.onTriggerClick;
    var formatText = useLocalization().formatText;
    return (React.createElement(UitkMenu, { className: getClassNameList('uitk-breadcrumbs-menu'), isOpen: isOpen, onTriggerClick: onTriggerClick },
        React.createElement(UitkMenuTrigger, null,
            React.createElement(UitkButton, { "aria-label": isOpen ? formatText('breadcrumbs.showLess') : formatText('breadcrumbs.showMore'), className: getClassNameList('uitk-breadcrumbs-menu-button'), analytics: analytics },
                React.createElement(UitkIcon, { name: "more_horiz" }))),
        React.createElement(UitkMenuContainer, { position: PositionType.LEFT },
            React.createElement(UitkMenuList, { items: crumbs.map(function (item) { return ({
                    Link: item,
                    attributes: {
                        role: 'menuItem',
                        tabindex: 0
                    },
                    className: 'uitk-list-item'
                }); }) }))));
};
var Breadcrumbs = React.forwardRef(function (props, ref) {
    var menuCrumbs = props.menuCrumbs, inlineCrumbs = props.inlineCrumbs, className = props.className, _a = props.showOnlySingleLine, showOnlySingleLine = _a === void 0 ? false : _a, menuAnalytics = props.menuAnalytics, _b = props.isOpen, isOpen = _b === void 0 ? false : _b, _c = props.onTriggerClick, onTriggerClick = _c === void 0 ? function () {
        /* Supply default if not provided */
    } : _c, _d = props.reverseOrderAndWrapElements, reverseOrderAndWrapElements = _d === void 0 ? false : _d;
    var breadcrumbListItemsRef = useRef([]);
    useImperativeHandle(ref, function () { return ({
        getBreadcrumbItems: function () { return breadcrumbListItemsRef.current; }
    }); });
    var cssClassNames = getClassNameList('uitk-breadcrumbs uitk-type-200', className);
    var listClassNames = getClassNameList('uitk-breadcrumbs-list', {
        'uitk-breadcrumbs-list-single': showOnlySingleLine,
        'uitk-breadcrumbs-list-reverse': reverseOrderAndWrapElements
    });
    var overflowMenu = (menuCrumbs === null || menuCrumbs === void 0 ? void 0 : menuCrumbs.length) > 0;
    var overflowMenuMarkup = (React.createElement("li", { className: getClassNameList('uitk-breadcrumbs-overflow-menu') },
        React.createElement(BreadcrumbsMenu, { crumbs: menuCrumbs.slice().reverse(), analytics: menuAnalytics, isOpen: isOpen, onTriggerClick: onTriggerClick }),
        React.createElement(UitkIcon, { size: UitkIconSize.SMALL, name: "chevron_right" })));
    // Don't show the arrow on the last visual breadcrumb. When the order is reversed via
    // flexbox, the last visual element is the first <li>. Otherwise it's the last.
    var lastVisualChevronIndex = reverseOrderAndWrapElements ? 0 : inlineCrumbs.length - 1;
    return (React.createElement("nav", { "aria-label": "breadcrumb", className: cssClassNames },
        React.createElement("ol", { className: listClassNames },
            overflowMenu && overflowMenuMarkup,
            inlineCrumbs.map(function (item, index) { return (React.createElement("li", { className: getClassNameList('uitk-breadcrumbs-item'), key: item + "-" + index, ref: function (el) {
                    breadcrumbListItemsRef.current[index] = el;
                } },
                item,
                index !== lastVisualChevronIndex && React.createElement(UitkIcon, { size: UitkIconSize.SMALL, name: "chevron_right" }))); }))));
});
var DynamicBreadcrumbs = function (props) {
    var crumbs = props.crumbs, className = props.className, menuAnalytics = props.menuAnalytics, isOpen = props.isOpen, _a = props.onTriggerClick, onTriggerClick = _a === void 0 ? function () {
        /* Supply default if not provided */
    } : _a;
    var breadcrumbItemsRef = useRef(null);
    // In order to determine how many breadcrumb elements can fit, we use a little hack.
    // Usually when flex items wrap, the end of the line wraps to the second line.
    // However, with breadcrumbs, we need the front of the line to wrap. To do this,
    // on load we render the breadcrumbs in flex-reverse. Then, we record how many items fit
    // on one line, then we render back in regular order with just those items. This is
    // done to preserve accessibility so that tabbing happens in the correct order.
    var _b = React.useState(true), isReverseWrapped = _b[0], setIsReverseWrapped = _b[1];
    var _c = React.useState(true), showOnlySingleLine = _c[0], setShowOnlySingleLine = _c[1];
    var _d = React.useState(1), numberOverflowCrumbs = _d[0], setNumberOverflowCrumbs = _d[1];
    React.useEffect(function () {
        var _a, _b;
        var breadcrumbItems = (_a = breadcrumbItemsRef.current) === null || _a === void 0 ? void 0 : _a.getBreadcrumbItems();
        if (breadcrumbItems && isReverseWrapped) {
            var _c = Array(3).fill(0), crumbsBeingDisplayed_1 = _c[0], firstListItemTop_1 = _c[1], currentListItemTop_1 = _c[2];
            firstListItemTop_1 = (_b = breadcrumbItems[0]) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().top;
            breadcrumbItems.forEach(function (item) {
                currentListItemTop_1 = item === null || item === void 0 ? void 0 : item.getBoundingClientRect().top;
                if (firstListItemTop_1 > currentListItemTop_1 - 5)
                    crumbsBeingDisplayed_1 += 1;
            });
            setNumberOverflowCrumbs(crumbs.length - crumbsBeingDisplayed_1);
            setIsReverseWrapped(false);
        }
        setShowOnlySingleLine(false);
    }, [isReverseWrapped, crumbs, breadcrumbItemsRef, setNumberOverflowCrumbs, setIsReverseWrapped]);
    return (React.createElement(Breadcrumbs, { className: className, ref: breadcrumbItemsRef, menuCrumbs: crumbs.slice(0, numberOverflowCrumbs), 
        // When we render in the reverse order, we reverse the breadcrumbs array so the user
        // sees the correct order. When we're not in reverse mode, we only render the amount of
        // breadcrumbs the screen has space for.
        inlineCrumbs: isReverseWrapped ? crumbs.slice().reverse() : crumbs.slice(numberOverflowCrumbs), isOpen: isOpen, onTriggerClick: onTriggerClick, menuAnalytics: menuAnalytics, showOnlySingleLine: showOnlySingleLine, reverseOrderAndWrapElements: isReverseWrapped }));
};
var UitkBreadcrumbs = function (props) {
    var crumbs = props.crumbs, className = props.className, _a = props.hasDynamicInlineCrumbs, hasDynamicInlineCrumbs = _a === void 0 ? false : _a, menuAnalytics = props.menuAnalytics, isOpen = props.isOpen, _b = props.onTriggerClick, onTriggerClick = _b === void 0 ? function () {
        /* Supply default if not provided */
    } : _b;
    if (crumbs.length < 2) {
        console.warn("Need to pass in at least two crumbs.");
        return null;
    }
    var menuCrumbs = crumbs.length >= 3 ? crumbs.slice(0, crumbs.length - 2) : [];
    var inlineCrumbs = crumbs.length >= 3 ? crumbs.slice(crumbs.length - 2) : crumbs;
    return hasDynamicInlineCrumbs ? (React.createElement(DynamicBreadcrumbs, __assign({}, props))) : (React.createElement(Breadcrumbs, { className: className, menuCrumbs: menuCrumbs, inlineCrumbs: inlineCrumbs, isOpen: isOpen, onTriggerClick: onTriggerClick, menuAnalytics: menuAnalytics }));
};
UitkBreadcrumbs.defaultProps = {
    hasDynamicInlineCrumbs: false,
    isOpen: false
};
UitkBreadcrumbs.displayName = 'UitkBreadcrumbs';

export { UitkBreadcrumbs };
